import {DestroyRef, inject, Injectable} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {catchError, tap} from 'rxjs/operators';
import {ConfigService} from '../config/service/config.service';
import {ServiceAvailabilityService} from '../services/service-availability.service';
import {apiAreaOf, endpointKey} from '../utils/api-url.util';


/**
 * Meldet fehlgeschlagene Requests an den Export-Service an den
 * ServiceAvailabilityService, damit der Nutzer einen Hinweis erhält.
 *
 * Der Interceptor beobachtet nur: Fehler werden unverändert weitergeworfen,
 * sodass jede bestehende Behandlung - insbesondere das
 * `catchError(() => of(null))` der Admin-Listen und die Failure-Actions der
 * Wizard-Effects - unverändert weiterläuft.
 *
 * Bereichsabhängiges Verhalten:
 *
 * - Admin (/admin/...): nur GET und HEAD, dafür alle Statuswerte. Die
 *   Admin-Listen verschlucken jeden Status, dort wäre selbst ein 403 sonst
 *   unsichtbar. Schreiboperationen bleiben aussen vor, weil die Meldung vom
 *   Daten-Abruf spricht und diese Aufrufe eigene Snackbars haben.
 * - Endkunde: alle Methoden, dafür nur Status 0. Zentrale Wizard-Schritte
 *   laufen als POST (Login, createExportConfigRequest), weshalb ein
 *   Methodenfilter dort Lücken liesse. Alle übrigen Statuswerte behandeln die
 *   Effects fachlich - teils als Prozessweiche, etwa der 409 in
 *   checkIfConfigExists, der den Wizard regulär weiterführt. Eine generische
 *   Meldung wäre dort verwirrend.
 */
@Injectable()
export class ServiceErrorInterceptor
  implements HttpInterceptor {

  private readonly _destroyRef = inject(DestroyRef);
  private readonly _configService = inject(ConfigService);
  private readonly _availabilityService = inject(ServiceAvailabilityService);

  private _apiBaseUrl?: string;

  constructor() {
    this._configService.loaded$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe(config => {
        this._apiBaseUrl = config.exportConfig?.apiBaseUrl;
      });
  }

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {

    const apiBaseUrl = this._apiBaseUrl;

    // INFO: Vor dem ersten Config-Wert unverändert durchreichen. Die
    // Konfiguration selbst wird über HttpClient geladen und läuft damit durch
    // diese Kette, bevor die API-Basis bekannt ist.
    if (!apiBaseUrl) {
      return next.handle(request);
    }

    const area = apiAreaOf(request.url, apiBaseUrl);
    if (!area) {
      return next.handle(request);
    }

    if (area === 'admin' && request.method !== 'GET' && request.method !== 'HEAD') {
      return next.handle(request);
    }

    const key = endpointKey(request.url, apiBaseUrl);
    if (!key) {
      return next.handle(request);
    }

    return next.handle(request)
      .pipe(
        tap(event => {
          // INFO: Nur der finale HttpResponse gilt als Erfolg - Sent- und
          // Progress-Events dürfen keine aktiven Fehler löschen.
          if (event instanceof HttpResponse) {
            this._availabilityService.reportSuccess(key);
          }
        }),
        catchError((error: unknown) => {
          if (!(error instanceof HttpErrorResponse)) {
            return throwError(() => error);
          }

          if (area === 'customer' && error.status !== 0) {
            return throwError(() => error);
          }

          this._availabilityService.reportFailure(area, error.status, key);

          return throwError(() => error);
        }),
      );
  }
}
